import moment from 'moment';
import {
  systolicBloodPressurePk,
  diastolicBloodPressurePk,
  minAndMaxValues,
  plotColors,
  minMaxPlotColors,
} from 'config.jsx';

class MeasureConverter {
  static convertMeasureObjectToPlotObject(measureObject, index, newAxisIndex) {
    const xValues = measureObject.map(measure => measure.date);
    const yValues = measureObject.map(measure => measure.value);
    const text = yValues.map(value => `${value}`);
    let plotDataObject = {
      x: xValues,
      y: yValues,
      type: 'scatter',
      mode: 'lines+markers',
      marker: { size: 4 },
      text,
      hoverinfo: 'text',
    };
    plotDataObject = index >= newAxisIndex
      ? { ...plotDataObject, ...{ yaxis: 'y2', line: { color: plotColors[1], width: 1 } } }
      : { ...plotDataObject, ...{ line: { color: plotColors[0], width: 1 } } };
    return plotDataObject;
  }

  static createNewEndpointsForMinMaxPlots(date) {
    const xValues = [];
    const measureDate = moment(date, 'YYYY-MM-DD');
    xValues[0] = moment(measureDate).subtract(1, 'days').format('YYYY-MM-DD');
    xValues[1] = measureDate.add(1, 'days').format('YYYY-MM-DD');
    return xValues;
  }

  static createMinAndMaxPlots(measureObject, measureObjectIndex, newAxisIndex) {
    let xValues = measureObject
      .filter((measure, index) => index === 0 || index === measureObject.length - 1)
      .map(measure => measure.date);
    const onlyOneMeasureDate = xValues.length === 1;
    if (onlyOneMeasureDate) {
      xValues = this.createNewEndpointsForMinMaxPlots(xValues[0]);
    }
    const minMaxValuesObject = minAndMaxValues.find(object => object.pk === measureObject[0].code);
    const minValues = xValues.map(() => minMaxValuesObject.min);
    const maxValues = xValues.map(() => minMaxValuesObject.max);
    const plotObject = {
      x: xValues,
      type: 'scatter',
      mode: 'lines',
      hoverinfo: 'skip',
      line: { width: 1, dash: 'dot' },
    };
    const minValuesPlotObject = measureObjectIndex < newAxisIndex
      ? { ...plotObject, y: minValues, line: { ...plotObject.line, color: minMaxPlotColors[0] } }
      : {
        ...plotObject, y: minValues, line: { ...plotObject.line, color: minMaxPlotColors[1] }, yaxis: 'y2',
      };
    const maxValuesPlotObject = measureObjectIndex < newAxisIndex
      ? { ...plotObject, y: maxValues, line: { ...plotObject.line, color: minMaxPlotColors[0] } }
      : {
        ...plotObject, y: maxValues, line: { ...plotObject.line, color: minMaxPlotColors[1] }, yaxis: 'y2',
      };
    return [minValuesPlotObject, maxValuesPlotObject];
  }

  static convertMeasureObjectsToPlotObjects(measureObjects, newAxisIndex) {
    const plotObjects = [];
    measureObjects.forEach((measureObject, index) => {
      plotObjects.push(this
        .convertMeasureObjectToPlotObject(measureObject, index, newAxisIndex));
      if (minAndMaxValues.map(valueObject => valueObject.pk).includes(measureObject[0].code)) {
        plotObjects.push(...this.createMinAndMaxPlots(measureObject, index, newAxisIndex));
      }
    });
    return plotObjects;
  }

  static convertBloodCellObjectsToPlotDataObjects(bloodCellMeasures, measureCodes) {
    const totalBloodCellAmountMeasures = bloodCellMeasures
      .filter(measureArray => measureArray[0].code === 17);
    const bloodCellPercentageMeasures = bloodCellMeasures
      .filter(measureArray => measureArray[0].code !== 17);
    const bloodCellPercentageNames = measureCodes
      .filter(measureCode => measureCode.pk !== 17).map(measureCode => measureCode.label.name);
    const bloodCellPlotObjects = bloodCellPercentageMeasures
      .map((singleTypeBloodCellMeasures, index) => {
        const bloodCellType = bloodCellPercentageNames[index];
        return this.convertBloodCellObjectToPlotDataObject(
          singleTypeBloodCellMeasures, bloodCellType, totalBloodCellAmountMeasures, index,
        );
      });
    return this.stackedArea(bloodCellPlotObjects);
  }

  static convertBloodCellObjectToPlotDataObject(
    singleTypeBloodCellMeasures, bloodCellType, totalBloodCellAmountMeasures, bloodCellTypeIndex,
  ) {
    const x = singleTypeBloodCellMeasures.map(measure => measure.date);
    const y = singleTypeBloodCellMeasures.map((measure, singleIndex) => (
      measure.value * totalBloodCellAmountMeasures[0][singleIndex].value
    ));
    const fill = bloodCellTypeIndex === 0 ? 'tozeroy' : 'tonexty';
    const marker = { size: 4 };
    const line = {
      width: 1,
    };
    const text = singleTypeBloodCellMeasures.map((measure, singleIndex) => (
      `${bloodCellType}: ${measure.value * totalBloodCellAmountMeasures[0][singleIndex].value} (${measure.value}%)`
    ));
    const hoverinfo = 'text';
    const plotDataObject = {
      x, y, fill, marker, line, text, hoverinfo,
    };
    return plotDataObject;
  }

  static stackedArea(traces) {
    const newTraces = traces;
    for (let i = 1; i < newTraces.length; i += 1) {
      for (let j = 0; j < (Math.min(newTraces[i].y.length, newTraces[i - 1].y.length)); j += 1) {
        newTraces[i].y[j] += newTraces[i - 1].y[j];
      }
    }
    return newTraces;
  }

  static createCorrelationPlotDataObject(measureObjects) {
    const xValues = measureObjects[0].map(measure => measure.value);
    const yValues = measureObjects[1].map(measure => measure.value);
    const text = measureObjects[0].map(measure => measure.date);
    const plotDataObject = {
      x: xValues,
      y: yValues,
      type: 'scatter',
      mode: 'markers',
      marker: { size: 4 },
      hoverinfo: 'x+y+text',
      text,
    };
    return plotDataObject;
  }

  static setMinMaxValues(measureObject, measureCodeMinMax, otherMeasureCodeHasMinAndMaxValues) {
    const measureMinMax = measureObject
      .map(measure => measure.value)
      .sort((a, b) => a - b)
      .filter((value, index) => index === 0 || index === measureObject.length - 1);
    if (otherMeasureCodeHasMinAndMaxValues) {
      if (measureMinMax[0] > measureCodeMinMax.min) {
        measureMinMax[0] = measureCodeMinMax.min;
      }
      if (measureMinMax[1] < measureCodeMinMax.max) {
        measureMinMax[1] = measureCodeMinMax.max;
      }
    } else if (measureMinMax[0] === measureMinMax[1]) {
      measureMinMax[0] -= 1;
      measureMinMax[1] += 1;
    }
    return measureMinMax;
  }

  static createMinMaxPlotsForOneMeasureCode(
    measureObject, currentMeasureCodeIndex, currentMeasureCodeMinMax, otherMeasureCodeMinMax,
  ) {
    const measureMinMaxValues = minAndMaxValues
      .map(valueObject => valueObject.pk).includes(measureObject[0].code)
      ? this.setMinMaxValues(measureObject, otherMeasureCodeMinMax, true)
      : this.setMinMaxValues(measureObject, otherMeasureCodeMinMax, false);
    const minValues = measureMinMaxValues.map(() => currentMeasureCodeMinMax.min);
    const maxValues = measureMinMaxValues.map(() => currentMeasureCodeMinMax.max);
    const plotObject = {
      type: 'scatter',
      mode: 'lines',
      hoverinfo: 'skip',
      line: { width: 1, dash: 'dot' },
    };
    const minValuePlotObject = currentMeasureCodeIndex === 0
      ? {
        ...plotObject,
        y: measureMinMaxValues,
        x: minValues,
        line: { ...plotObject.line, color: minMaxPlotColors[0] },
      }
      : {
        ...plotObject,
        x: measureMinMaxValues,
        y: minValues,
        line: { ...plotObject.line, color: minMaxPlotColors[1] },
      };
    const maxValuePlotObject = currentMeasureCodeIndex === 0
      ? {
        ...plotObject,
        y: measureMinMaxValues,
        x: maxValues,
        line: { ...plotObject.line, color: minMaxPlotColors[0] },
      }
      : {
        ...plotObject,
        x: measureMinMaxValues,
        y: maxValues,
        line: { ...plotObject.line, color: minMaxPlotColors[1] },
      };
    return [minValuePlotObject, maxValuePlotObject];
  }

  static createMinAndMaxPlotsForCorrelationPlot(measureObjects) {
    const minMaxPlots = [];
    const firstMeasureCodeMinMax = minAndMaxValues
      .find(object => object.pk === measureObjects[0][0].code);
    const secondMeasureCodeMinMax = minAndMaxValues
      .find(object => object.pk === measureObjects[1][0].code);
    if (minAndMaxValues.map(valueObject => valueObject.pk).includes(measureObjects[0][0].code)) {
      minMaxPlots.push(...this.createMinMaxPlotsForOneMeasureCode(
        measureObjects[1], 0, firstMeasureCodeMinMax, secondMeasureCodeMinMax,
      ));
    }
    if (minAndMaxValues.map(valueObject => valueObject.pk).includes(measureObjects[1][0].code)) {
      minMaxPlots.push(...this.createMinMaxPlotsForOneMeasureCode(
        measureObjects[0], 1, secondMeasureCodeMinMax, firstMeasureCodeMinMax,
      ));
    }
    return minMaxPlots;
  }

  static createCorrelationPlotDataObjects(measureObjects) {
    const plotDataObjects = [];
    plotDataObjects.push(this.createCorrelationPlotDataObject(measureObjects));
    plotDataObjects.push(...this.createMinAndMaxPlotsForCorrelationPlot(measureObjects));
    return plotDataObjects;
  }

  static createCorrelationPlotLayoutObject(measureCodes, title) {
    const plotLayout = {
      autosize: true,
      showlegend: false,
      xaxis: {
        title: `${measureCodes[0].label.name} (${measureCodes[0].unit})`,
        titlefont: { color: plotColors[0] },
      },
      yaxis: {
        title: `${measureCodes[1].label.name} (${measureCodes[1].unit})`,
        titlefont: { color: plotColors[1] },
      },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0.05)',
      hovermode: 'closest',
      title,
    };
    return plotLayout;
  }

  static createBloodCellPlotLayoutObject() {
    const plotLayout = {
      autosize: true,
      showlegend: false,
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0.05)',
      yaxis: { hoverformat: '0f', title: 'White blood cells (million/L)' },
      xaxis: { title: 'Date' },
    };
    return plotLayout;
  }

  static createPlotTitleForWarnings(measureObjects, measureCodes) {
    const hasAnyMeasureMinAndMaxValues = this.hasAnyMeasureMinAndMaxValues(measureCodes);
    if (hasAnyMeasureMinAndMaxValues) {
      const measureCodesThatHaveValuesOutOfNormal = this
        .getMeasureCodesWithValuesOutOfNormal(measureObjects, measureCodes);
      if (measureCodesThatHaveValuesOutOfNormal.length > 0) {
        return this.convertMeasureCodesToWarningTitle(measureCodesThatHaveValuesOutOfNormal);
      }
    }
    return '';
  }

  static convertMeasureCodesToWarningTitle(measureCodes) {
    const measureLabels = measureCodes.map(measureCode => `${measureCode.label.name} out of normal.`).join('<br>');
    return measureLabels;
  }

  static hasAnyMeasureMinAndMaxValues(measureCodes) {
    const measureCodePks = measureCodes.map(measureCode => measureCode.pk);
    const measureCodePksWithMinMaxValues = minAndMaxValues
      .map(minMaxValueObject => minMaxValueObject.pk);
    return measureCodePks.some(measureCode => measureCodePksWithMinMaxValues.includes(measureCode));
  }

  static getMeasureCodesWithValuesOutOfNormal(measureObjects, measureCodes) {
    const selectedMeasureCodePks = measureCodes.map(measureCode => measureCode.pk);
    const measureCodePksWithMinMaxValues = minAndMaxValues
      .map(minMaxValueObject => minMaxValueObject.pk);
    const selectedMeasureCodePksWithMinAndMaxValues = selectedMeasureCodePks
      .filter(measureCodePk => measureCodePksWithMinMaxValues.includes(measureCodePk));
    const lastThreeMeasureValues = measureObjects
      .filter(measureObject => measureCodePksWithMinMaxValues.includes(measureObject[0].code))
      .map(measureObject => measureObject
        .filter((_, index) => index >= measureObject.length - 3)
        .map(singleMeasure => singleMeasure.value));
    const measureObjectsMinMaxValues = selectedMeasureCodePksWithMinAndMaxValues
      .map(measureCodePk => minAndMaxValues
        .find(minMaxValuesObject => minMaxValuesObject.pk === measureCodePk));
    const measureCodePksThatHaveValuesOutOfNormal = measureObjectsMinMaxValues
      .filter((measureObjectMinMaxValues, index) => (
        lastThreeMeasureValues[index]
          .some(value => (
            value < measureObjectMinMaxValues.min || value > measureObjectMinMaxValues.max
          ))
      ))
      .map(minMaxValuesObject => minMaxValuesObject.pk);
    return measureCodePksThatHaveValuesOutOfNormal
      .map(measureCodePk => measureCodes.find(measureCode => measureCode.pk === measureCodePk));
  }

  static createPlotLayoutObject(measureCodesWithNewAxisIndex, title) {
    const measureCodes = measureCodesWithNewAxisIndex[0];
    const newAxisIndex = measureCodesWithNewAxisIndex[1];
    let plotLayout = {
      autosize: true,
      yaxis: {
        titlefont: { color: plotColors[0] },
      },
      showlegend: false,
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0.05)',
      xaxis: { title: 'Date' },
      title,
    };
    plotLayout.yaxis = measureCodes[0].pk === systolicBloodPressurePk
      || measureCodes[0].pk === diastolicBloodPressurePk
      ? { ...plotLayout.yaxis, ...{ title: 'Blood pressure (mmHg)' } }
      : { ...plotLayout.yaxis, ...{ title: `${measureCodes[0].label.name} (${measureCodes[0].unit})` } };
    plotLayout.yaxis = measureCodes[0].unit === 'percent'
      ? { ...plotLayout.yaxis, ...{ range: [0, 100] } }
      : { ...plotLayout.yaxis, ...{ rangemode: 'tozero' } };
    if (measureCodes.length > newAxisIndex) {
      const yaxis2 = {
        yaxis2: {
          titlefont: { color: plotColors[1] },
          overlaying: 'y',
          side: 'right',
          showgrid: false,
          showticklabels: true,
        },
      };
      yaxis2.yaxis2 = measureCodes[newAxisIndex].pk === systolicBloodPressurePk
        || measureCodes[newAxisIndex].pk === diastolicBloodPressurePk
        ? { ...yaxis2.yaxis2, ...{ title: 'Blood pressure (mmHg)' } }
        : { ...yaxis2.yaxis2, ...{ title: `${measureCodes[newAxisIndex].label.name} (${measureCodes[newAxisIndex].unit})` } };
      yaxis2.yaxis2 = measureCodes[newAxisIndex].unit === 'percent'
        ? { ...yaxis2.yaxis2, ...{ range: [0, 100] } }
        : { ...yaxis2.yaxis2, ...{ rangemode: 'tozero' } };
      plotLayout = { ...plotLayout, ...yaxis2 };
    }
    return plotLayout;
  }
}

export default MeasureConverter;



// WEBPACK FOOTER //
// ./src/components/health-graph/MeasureConverter.jsx